/* ==========================================================================
   AURA — CART CONFIRMATION (add + "added to your collection")
   AURA owns the add: intercept the product form's submit (capture phase, so
   Dawn's product-form.js — which adds the item but then throws before
   publishing cartUpdate — never double-adds), POST /cart/add ourselves, then
   reveal the confirmation: a hero of the just-added product, free-shipping
   progress, a dominant checkout, a collapsible cart summary (line items with
   remove ×, subtotal), a refreshed header count, and `related` recommendations.
   Overlay is looked up fresh each time so it survives product-info.js re-renders.
   ========================================================================== */
(function () {
  'use strict';

  const ID = 'AuraCartAdded';
  const el = () => document.getElementById(ID);
  const CART = () => (window.routes ? window.routes.cart_url : '/cart');
  const ADD = () => (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  const CURRENCY = () =>
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  let lastFocus = null;

  /* ---------- money ---------- */
  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'sl', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  }
  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  /* ---------- open / close ---------- */
  function open() {
    const overlay = el();
    if (!overlay) return;
    lastFocus = document.activeElement;
    overlay.removeAttribute('hidden');
    document.body.classList.add('aura-cart-added-open');
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    const closeBtn = overlay.querySelector('.aura-cart-added__close');
    if (closeBtn) closeBtn.focus();
  }
  function close() {
    const overlay = el();
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.classList.remove('aura-cart-added-open');
    window.setTimeout(() => overlay.setAttribute('hidden', ''), 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener('keydown', (e) => {
    const overlay = el();
    if (e.key === 'Escape' && overlay && overlay.classList.contains('is-open')) close();
  });

  /* ---------- header count ---------- */
  function updateCount(count) {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    let bubble = link.querySelector('.cart-count-bubble');
    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        link.appendChild(bubble);
      }
      bubble.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count}</span>`;
    } else if (bubble) {
      bubble.remove();
    }
  }

  /* ---------- hero (the just-added product) ---------- */
  function renderHero(item) {
    const overlay = el();
    const hero = overlay && overlay.querySelector('[data-hero]');
    if (!hero) return;
    if (!item) {
      hero.innerHTML = '';
      return;
    }
    const imgUrl = (item.featured_image && item.featured_image.url) || item.image || '';
    const img = imgUrl
      ? `<img src="${sized(imgUrl, 400)}" alt="" width="96" height="96" loading="eager">`
      : '';
    const vendor = item.vendor
      ? `<p class="aura-cart-added__hero-vendor">${item.vendor}</p>`
      : '';
    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="aura-cart-added__hero-variant">${item.variant_title}</p>`
        : '';
    const cents = item.final_price != null ? item.final_price : item.price;
    hero.innerHTML =
      `<div class="aura-cart-added__hero-media">${img}</div>` +
      `<div class="aura-cart-added__hero-info">` +
      vendor +
      `<p class="aura-cart-added__hero-title">${item.product_title || item.title || ''}</p>` +
      variant +
      `<p class="aura-cart-added__hero-price">${money(cents, CURRENCY())}</p>` +
      `</div>`;
  }

  /* ---------- free-shipping progress ---------- */
  function renderShip(cart) {
    const overlay = el();
    const ship = overlay && overlay.querySelector('[data-ship]');
    if (!ship) return;
    const threshold = parseInt(overlay.dataset.freeShipping, 10) || 0;
    if (!threshold) {
      ship.setAttribute('hidden', '');
      return;
    }
    const text = ship.querySelector('[data-ship-text]');
    const fill = ship.querySelector('[data-ship-fill]');
    const total = (cart && cart.total_price) || 0;
    const cur = (cart && cart.currency) || CURRENCY();
    ship.removeAttribute('hidden');
    if (total >= threshold) {
      if (text) text.innerHTML = 'Čestitamo — odklenili ste <strong>brezplačno dostavo</strong>.';
      if (fill) fill.style.width = '100%';
      ship.classList.add('is-complete');
    } else {
      if (text)
        text.innerHTML =
          'Še <strong>' + money(threshold - total, cur) + '</strong> do brezplačne dostave';
      if (fill) fill.style.width = Math.max(4, Math.round((total / threshold) * 100)) + '%';
      ship.classList.remove('is-complete');
    }
  }

  /* ---------- render cart ---------- */
  function itemHTML(line, currency) {
    const img = line.image ? sized(line.image, 160) : '';
    const variant =
      line.variant_title && line.variant_title !== 'Default Title'
        ? `<span class="aura-cart-added__variant">${line.variant_title}</span>`
        : '';
    return (
      `<div class="aura-cart-added__item" data-key="${line.key}">` +
      `<a class="aura-cart-added__media" href="${line.url}">${img ? `<img src="${img}" alt="" width="64" height="64" loading="lazy">` : ''}</a>` +
      `<div class="aura-cart-added__info">` +
      `<a class="aura-cart-added__name" href="${line.url}">${line.product_title}</a>` +
      variant +
      `<div class="aura-cart-added__line"><span class="aura-cart-added__q">${line.quantity} ×</span> ${money(line.final_price, currency)}</div>` +
      `</div>` +
      `<button type="button" class="aura-cart-added__remove" data-remove data-key="${line.key}" aria-label="Odstrani izdelek">` +
      `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>` +
      `</button>` +
      `</div>`
    );
  }

  async function renderCart() {
    const overlay = el();
    if (!overlay) return null;
    const wrap = overlay.querySelector('[data-cart-items]');
    const sub = overlay.querySelector('[data-cart-subtotal]');
    const cnt = overlay.querySelector('[data-cart-count]');
    let cart;
    try {
      cart = await fetch(`${CART()}.js`).then((r) => r.json());
    } catch (e) {
      return null;
    }
    if (wrap) {
      if (!cart.items || !cart.items.length) {
        wrap.innerHTML = '<p class="aura-cart-added__empty">Vaša košarica je prazna.</p>';
      } else {
        wrap.innerHTML = cart.items.map((l) => itemHTML(l, cart.currency)).join('');
      }
    }
    if (sub) sub.textContent = money(cart.total_price, cart.currency);
    if (cnt) cnt.textContent = cart.item_count ? ' · ' + cart.item_count : '';
    renderShip(cart);
    updateCount(cart.item_count);
    return cart;
  }

  /* remove (delegated) */
  document.addEventListener('click', async (e) => {
    const overlay = el();
    if (!overlay || !overlay.contains(e.target)) return;
    if (e.target.closest('[data-close]')) return close();

    /* quick-add a recommended product */
    const addBtn = e.target.closest('[data-rec-add]');
    if (addBtn) {
      const id = addBtn.dataset.variantId;
      if (!id || addBtn.getAttribute('aria-disabled') === 'true') return;
      addBtn.setAttribute('aria-disabled', 'true');
      try {
        const res = await fetch(ADD(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({ id: id, quantity: 1 }),
        });
        if (res.ok) {
          await renderCart();
          addBtn.classList.add('is-added');
          window.setTimeout(() => {
            addBtn.classList.remove('is-added');
            addBtn.setAttribute('aria-disabled', 'false');
          }, 1400);
        } else {
          addBtn.setAttribute('aria-disabled', 'false');
        }
      } catch (err) {
        addBtn.setAttribute('aria-disabled', 'false');
      }
      return;
    }

    const btn = e.target.closest('[data-remove]');
    if (!btn) return;
    const item = btn.closest('.aura-cart-added__item');
    if (item) item.classList.add('is-removing');
    try {
      await fetch(`${CART()}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: btn.dataset.key, quantity: 0 }),
      });
    } catch (err) {
      /* ignore */
    }
    renderCart();
  });

  /* ---------- recommendations ---------- */
  async function loadRecs(productId) {
    const overlay = el();
    const wrap = overlay && overlay.querySelector('[data-recs]');
    const list = overlay && overlay.querySelector('[data-recs-list]');
    const recUrl = overlay && overlay.dataset.recommendationsUrl;
    if (!wrap || !list) return;
    wrap.setAttribute('hidden', '');
    list.innerHTML = '';
    if (!recUrl || !productId) return;
    try {
      const url = `${recUrl}?section_id=aura-cart-recommendations&product_id=${productId}&intent=related&limit=4`;
      const text = await fetch(url).then((r) => r.text());
      const inner = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-aura-recs]');
      if (inner && inner.innerHTML.trim().length) {
        list.innerHTML = inner.innerHTML;
        wrap.removeAttribute('hidden');
      }
    } catch (e) {
      /* optional */
    }
  }

  function reveal(item) {
    open();
    renderHero(item);
    renderCart();
    const productId = (item && item.product_id) || (el() && el().dataset.productId);
    loadRecs(productId);
  }

  /* ---------- AURA owns the add ---------- */
  async function addToCart(form) {
    const container = form.closest('.aura-product-form') || form;
    const btn =
      container.querySelector('[type="submit"][name="add"]') ||
      document.querySelector(`[type="submit"][form="${form.id}"]`) ||
      form.querySelector('[type="submit"]');
    const spinner = btn && btn.querySelector('.loading__spinner');
    const setLoading = (on) => {
      if (!btn) return;
      btn.setAttribute('aria-disabled', on ? 'true' : 'false');
      btn.classList.toggle('loading', on);
      if (spinner) spinner.classList.toggle('hidden', !on);
    };
    const errWrap = container.querySelector('.product-form__error-message-wrapper');
    if (errWrap) errWrap.setAttribute('hidden', '');
    setLoading(true);
    try {
      const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: new FormData(form),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        if (errWrap) {
          errWrap.removeAttribute('hidden');
          const m = errWrap.querySelector('.product-form__error-message');
          if (m) m.textContent = data.description || data.message || 'Napaka pri dodajanju v košarico.';
        }
        return;
      }
      const item = data.items ? data.items[0] : data;
      reveal(item);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.matches || !form.matches('form.aura-product-form__form')) return;
      e.preventDefault();
      e.stopPropagation();
      addToCart(form);
    },
    true
  );

  /* other adders (Frequently-Bought sets, etc.) */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source === 'aura-cart-added') return;
      const raw = event.cartData || {};
      const item = raw.product_id ? raw : (raw.items && raw.items[0]) || raw;
      reveal(item);
    });
  }
})();
